import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Features = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="features" className="section bg-gray-50">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Easy to Set Up, <span className="text-orange-dark-si">Powerful Results</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Sales Intercept is designed to be simple to implement while delivering 
            professional results that drive conversions. Perfect for any industry, including our specialized <a href="/real-estate" className="text-purple-si hover:text-purple-si/90 underline">real-estate solution</a>.
          </p>
        </div>

        <motion.div
          ref={ref}
          className="space-y-24"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Feature 1 */}
          <motion.div 
            className="flex flex-col gap-12"
            variants={itemVariants}
          >
            <div>
              <h3 className="text-2xl font-bold mb-4">Fast & Easy Installation</h3>
              <p className="text-gray-700 mb-6">
                Add Sales Intercept to your website with a single line of code, similar to adding Google 
                Analytics or a Facebook Pixel. No technical expertise required.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>One-time code embed in your website header</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Works with any website platform (WordPress, Shopify, Wix, etc.)</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>No programming knowledge needed</span>
                </li>
              </ul>
            </div>
            <div className="relative rounded-lg overflow-hidden shadow-xl bg-gray-50 p-8">
              <div className="bg-white/90 rounded-lg p-3 shadow-lg">
                <pre className="text-xs text-gray-800 font-mono">
                  &lt;script src="https://cdn.salesintercept.com/widget.js"&gt;&lt;/script&gt;
                </pre>
              </div>
            </div>
          </motion.div>

          {/* Feature 2 */}
          <motion.div 
            className="flex flex-col gap-12"
            variants={itemVariants}
          >
            <div>
              <h3 className="text-2xl font-bold mb-4">Use Your Social Media Videos</h3>
              <p className="text-gray-700 mb-6">
                Already creating great content for social media? Put it to work on your website. 
                Import your existing videos from TikTok, Instagram, and other platforms directly 
                into Sales Intercept.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Import videos from TikTok, Instagram, and more</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Leverage your existing content strategy</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Automatically optimize for web performance</span>
                </li>
              </ul>
            </div>
            <div className="relative rounded-lg overflow-hidden shadow-xl bg-gray-50 p-8">
              <div className="bg-white/90 rounded-lg p-3 shadow-lg">
                <h4 className="text-lg font-semibold mb-2">Use Your Existing Content</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">📱</span>
                    <span>TikTok</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">📸</span>
                    <span>Instagram</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🎥</span>
                    <span>YouTube</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feature 3 */}
          <motion.div 
            className="flex flex-col gap-12"
            variants={itemVariants}
          >
            <div>
              <h3 className="text-2xl font-bold mb-4">Fully Hosted Solution</h3>
              <p className="text-gray-700 mb-6">
                Your videos and widget are hosted on our high-performance cloud infrastructure.
                No need to worry about servers, bandwidth, or website performance.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Fast-loading videos that don't slow down your site</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Secure, reliable cloud infrastructure</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>Automatic updates and improvements</span>
                </li>
              </ul>
            </div>
            <div className="relative rounded-lg overflow-hidden shadow-xl bg-gray-50 p-8">
              <div className="flex items-center space-x-2 bg-white/90 rounded-lg p-3 shadow-lg">
                <div className="w-3 h-3 bg-green-500 rounded-full pulse-animation"></div>
                <span className="text-sm font-medium">Always online, lightning fast</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;